// ExplorePH3/client/src/components/MapComponent.jsx
import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Navigation, Target, ZoomIn, ZoomOut } from 'lucide-react';
import VenueCard from './VenueCard';

// Custom marker icons
const createCustomIcon = (vibe) => {
  let className = 'bg-gradient-to-r from-teal-500 to-teal-600';
  
  switch (vibe?.toLowerCase()) {
    case 'fire':
      className = 'bg-gradient-to-r from-red-500 to-orange-500 animate-pulse-fire';
      break;
    case 'chill':
      className = 'bg-gradient-to-r from-blue-500 to-teal-500 animate-pulse-chill';
      break;
    case 'dead':
      className = 'bg-gradient-to-r from-gray-500 to-gray-700';
      break;
  }

  return L.divIcon({
    html: `
      <div class="relative">
        <div class="w-10 h-10 ${className} rounded-full flex items-center justify-center shadow-lg transform -translate-x-1/2 -translate-y-1/2">
          <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-3 h-3 ${className} rotate-45"></div>
      </div>
    `,
    className: 'custom-marker',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
};

// Map controls component
const MapControls = ({ onLocate, onZoomIn, onZoomOut }) => {
  return (
    <div className="absolute bottom-24 right-4 z-[1000] flex flex-col gap-2">
      <button
        onClick={onLocate}
        className="w-12 h-12 bg-white rounded-full shadow-hard flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Locate me"
      >
        <Navigation className="w-6 h-6 text-teal-600" />
      </button>
      <button
        onClick={onZoomIn}
        className="w-12 h-12 bg-white rounded-full shadow-hard flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Zoom in"
      >
        <ZoomIn className="w-6 h-6 text-teal-600" />
      </button>
      <button
        onClick={onZoomOut}
        className="w-12 h-12 bg-white rounded-full shadow-hard flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Zoom out"
      >
        <ZoomOut className="w-6 h-6 text-teal-600" />
      </button>
    </div>
  );
};

// User location marker component
const UserLocationMarker = ({ position }) => {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.flyTo(position, 15);
    }
  }, [position, map]);

  if (!position) return null;

  const userIcon = L.divIcon({
    html: `
      <div class="relative">
        <div class="w-12 h-12 bg-gradient-to-r from-sunset-500 to-sunset-600 rounded-full flex items-center justify-center shadow-lg transform -translate-x-1/2 -translate-y-1/2">
          <div class="w-8 h-8 bg-white rounded-full flex items-center justify-center">
            <svg class="w-4 h-4 text-sunset-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
        <div class="absolute inset-0 rounded-full border-4 border-sunset-500/30 animate-ping"></div>
      </div>
    `,
    className: 'user-marker',
    iconSize: [48, 48],
    iconAnchor: [24, 48],
  });

  return <Marker position={position} icon={userIcon} />;
};

// Map events handler
const MapEventHandler = ({ onMoveEnd }) => {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      const bounds = map.getBounds();
      onMoveEnd({
        center: [center.lat, center.lng],
        bounds: {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        },
        zoom: map.getZoom(),
      });
    },
  });
  return null;
};

const MapComponent = ({
  venues = [],
  userLocation = null,
  onVenueClick,
  onCheckIn,
  onMapMove,
  loading = false,
  className = '',
}) => {
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  // Default center (Manila)
  const defaultCenter = userLocation || [14.5995, 120.9842];
  const defaultZoom = userLocation ? 15 : 12;

  const handleLocate = () => {
    if (mapRef.current && userLocation) {
      mapRef.current.flyTo(userLocation, 16);
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const userPos = [latitude, longitude];
          if (mapRef.current) {
            mapRef.current.flyTo(userPos, 16);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
        }
      );
    }
  };

  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  };

  const handleMapCreated = (map) => {
    mapRef.current = map;
    setMapReady(true);
  };

  const handleMoveEnd = (mapData) => {
    if (onMapMove) {
      onMapMove(mapData);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/80 z-[1000] flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading venues...</p>
          </div>
        </div>
      )}

      {/* Map container */}
      <div className="absolute inset-0 rounded-2xl overflow-hidden">
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          className="h-full w-full"
          whenCreated={handleMapCreated}
          zoomControl={false}
          scrollWheelZoom={true}
          touchZoom={true}
          doubleClickZoom={true}
          dragging={true}
        >
          {/* CartoDB Voyager tiles - Premium look without API limits */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          />

          {/* Map event handler */}
          <MapEventHandler onMoveEnd={handleMoveEnd} />

          {/* User location marker */}
          <UserLocationMarker position={userLocation} />

          {/* Venue markers */}
          {venues.map((venue) => {
            if (!venue.latitude || !venue.longitude) return null;
            
            const position = [venue.latitude, venue.longitude];
            const icon = createCustomIcon(venue.current_vibe);

            return (
              <Marker
                key={venue.id}
                position={position}
                icon={icon}
                eventHandlers={{
                  click: () => onVenueClick && onVenueClick(venue),
                }}
              >
                <Popup className="venue-popup" maxWidth={300} minWidth={250}>
                  <div className="p-2">
                    <VenueCard
                      venue={venue}
                      onCheckIn={onCheckIn}
                      showCheckInButton={false}
                    />
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* Map controls */}
      {mapReady && (
        <MapControls
          onLocate={handleLocate}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
        />
      )}

      {/* Current location button */}
      <div className="absolute top-4 left-4 z-[1000]">
        <button
          onClick={handleLocate}
          className="bg-white px-4 py-2 rounded-full shadow-hard flex items-center gap-2 active:scale-95 transition-transform"
        >
          <Target className="w-4 h-4 text-teal-600" />
          <span className="text-sm font-medium text-gray-700">My Location</span>
        </button>
      </div>

      {/* Venue count badge */}
      {venues.length > 0 && (
        <div className="absolute top-4 right-4 z-[1000]">
          <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-full shadow-hard">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-teal-600" />
              <span className="text-sm font-medium text-gray-700">
                {venues.length} venue{venues.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Map attribution */}
      <div className="absolute bottom-2 left-2 z-[1000]">
        <div className="bg-white/80 backdrop-blur-sm px-2 py-1 rounded text-xs text-gray-600">
          © OpenStreetMap © CARTO
        </div>
      </div>
    </div>
  );
};

export default MapComponent;